import { FC, ReactNode, useContext, useEffect, useState, forwardRef } from 'react';
import {
	Box,
	Checkbox,
	FormControl,
	FormControlProps,
	FormErrorMessage,
	FormLabel,
	FormLabelProps,
	IconButton,
	Input,
	Text,
} from 'src/components/ui';
import DatePicker from 'react-datepicker';
import { FaRegCalendarAlt } from 'react-icons/fa';
import 'react-datepicker/dist/react-datepicker.css';
import './reactDatePicker.css';
import { ECampaignScheduleType } from 'src/lib/schemas/campaign/newFlowCampaign';
import { CampaignContext } from 'src/contexts';

export interface DatePickerInputProps {
	label?: string;
	id?: string;
	value?: Date;
	placeholder?: string;
	error?: ReactNode;
	onChange?: (date: Date | null) => void;
	minDate?: Date;
	name: string;
	isEndDate?: boolean;
	formControlProps?: FormControlProps;
	formLabelProps?: FormLabelProps;
	onChangeScheduleType?: (type: ECampaignScheduleType) => void;
}

const DatePickerInput: FC<DatePickerInputProps> = ({
	value,
	onChange,
	error,
	label,
	name,
	minDate,
	placeholder,
	formControlProps,
	formLabelProps,
	isEndDate,
	onChangeScheduleType,
}) => {
	const { campaign } = useContext(CampaignContext);
	const [isEndDateChecked, setIsEndDateChecked] = useState(
		campaign?.schedule?.type === ECampaignScheduleType['One-off'],
	);
	const hasError = !!error;

	useEffect(() => {
		campaign?.schedule?.type &&
			setIsEndDateChecked(
				campaign.schedule.type === ECampaignScheduleType['One-off'],
			);
	}, [campaign?.schedule?.type]);

	useEffect(() => {
		if (onChangeScheduleType) {
			onChangeScheduleType(
				isEndDateChecked
					? ECampaignScheduleType['One-off']
					: ECampaignScheduleType.Evergreen,
			);
		}
	}, [isEndDateChecked, onChangeScheduleType]);

	const CustomInput = forwardRef<HTMLInputElement, {
		value?: string;
		onClick?: () => void;
		onFocus?: () => void;
	}>(({ value, onClick, onFocus }, ref) => (
		<Box
			position="relative"
			display="flex"
			alignItems="center"
			w="full"
			cursor={isEndDate && !isEndDateChecked ? 'not-allowed' : 'pointer'}
		>
			<IconButton
				aria-label="Calendar"
				icon={<FaRegCalendarAlt />}
				size="sm"
				bg="transparent"
				position="absolute"
				left="8px"
				zIndex={1}
				onClick={onClick}
				_hover={{ bg: 'transparent' }}
			/>
			<Input
				ref={ref}
				fontSize="14px"
				value={value}
				onClick={onClick}
				onFocus={onFocus}
				onChange={() => {}} // Add empty onChange to prevent warning
				placeholder={placeholder}
				pl="40px"
				disabled={isEndDate && !isEndDateChecked}
				autoComplete="off"
				readOnly
				cursor="pointer"
				_disabled={{ cursor: 'not-allowed' }}
			/>
		</Box>
	));

	CustomInput.displayName = 'CustomInput';

	return (
		<FormControl isInvalid={hasError} {...formControlProps} overflow="visible">
			<FormLabel fontWeight={400} color={'black'} mb={2} {...formLabelProps}>
				{isEndDate ? (
					<Checkbox
						isChecked={isEndDateChecked}
						onChange={(e) => setIsEndDateChecked(e.target.checked)}
						data-tour="define-end-date"
					>
						<Text fontWeight={400} color={'black'}>
							Define an end date
						</Text>
					</Checkbox>
				) : (
					label
				)}
			</FormLabel>
			<DatePicker
				selected={value}
				onChange={(date) => {
					onChange && onChange(date);
				}}
				minDate={minDate || new Date()}
				customInput={<CustomInput />}
				dateFormat="dd/MM/yyyy h:mm aa"
				placeholderText={placeholder}
				showTimeSelect
				shouldCloseOnSelect
				disabled={isEndDate && !isEndDateChecked}
				popperPlacement="bottom-start"
				popperModifiers={[
					{
						name: 'preventOverflow',
						options: {
							rootBoundary: 'viewport',
							altAxis: true,
						},
					},
				]}
			/>
			{error && <FormErrorMessage>{error}</FormErrorMessage>}
		</FormControl>
	);
};

export default DatePickerInput;
